import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Languages, Loader2 } from "lucide-react";

// Every seeded account uses this password; letting users click-to-fill on the
// demo screen is fine here (test/staging build). In production the demo panel
// would be feature-flagged off.
const DEMO_PASSWORD = "hag@2026";

const DEMO_GROUPS = [
  {
    label_en: "Phase 1 · Order Tracker & Daura",
    label_hi: "फेज़ 1 · ऑर्डर ट्रैकर व दौरा",
    accounts: [
      { u: "director",   role_en: "Director",           role_hi: "डायरेक्टर" },
      { u: "supervisor", role_en: "Daura Supervisor",   role_hi: "दौरा सुपरवाइज़र" },
      { u: "coordinator",role_en: "Data Coordinator",   role_hi: "डेटा कोऑर्डिनेटर" },
      { u: "merch1",     role_en: "Merchandiser",       role_hi: "मर्चेंडाइज़र" },
      { u: "merch2",     role_en: "Merchandiser",       role_hi: "मर्चेंडाइज़र" },
      { u: "inspector1", role_en: "Inspector · Zone A", role_hi: "इंस्पेक्टर · ज़ोन A" },
      { u: "inspector2", role_en: "Inspector · Zone B", role_hi: "इंस्पेक्टर · ज़ोन B" },
      { u: "inspector3", role_en: "Inspector · Zone C", role_hi: "इंस्पेक्टर · ज़ोन C" },
    ],
  },
  {
    label_en: "Phase 2 · Factory (QC · Finishing · Despatch)",
    label_hi: "फेज़ 2 · फैक्ट्री (QC · फिनिशिंग · प्रेषण)",
    accounts: [
      { u: "qccoord",    role_en: "Prod & QC Coordinator", role_hi: "प्रोड व QC कोऑर्डिनेटर" },
      { u: "qcofficer",  role_en: "QC Officer",            role_hi: "QC ऑफिसर" },
      { u: "washmaster", role_en: "Wash Master",           role_hi: "वॉश मास्टर" },
      { u: "finisher1",  role_en: "Finisher",              role_hi: "फिनिशर" },
      { u: "finisher2",  role_en: "Finisher",              role_hi: "फिनिशर" },
      { u: "stores",     role_en: "Stores Coordinator",    role_hi: "स्टोर कोऑर्डिनेटर" },
    ],
  },
  {
    label_en: "Phase 3 · MG Rugs Workspace",
    label_hi: "फेज़ 3 · MG रग्स वर्कस्पेस",
    accounts: [
      { u: "mg1", role_en: "MG Rugs Workspace", role_hi: "MG रग्स वर्कस्पेस" },
    ],
  },
  {
    label_en: "Phase 4 · RIC Godown (Jangi Road, Mirzapur)",
    label_hi: "फेज़ 4 · RIC गोदाम (जंगी रोड)",
    accounts: [
      { u: "ricsuper", role_en: "RIC Supervisor",  role_hi: "RIC सुपरवाइज़र" },
      { u: "ric1",     role_en: "RIC Staff",       role_hi: "RIC स्टाफ़" },
      { u: "ric2",     role_en: "RIC Staff",       role_hi: "RIC स्टाफ़" },
    ],
  },
  {
    label_en: "Phase 5 · Gate & Security",
    label_hi: "फेज़ 5 · गेट व सुरक्षा",
    accounts: [
      { u: "guard1",   role_en: "Security Guard",  role_hi: "सिक्योरिटी गार्ड" },
    ],
  },
];

export default function Login() {
  const { login, user } = useAuth();
  const { lang, toggle, t } = useLang();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDemo, setShowDemo] = useState(false);

  React.useEffect(() => {
    if (user) nav(defaultRouteFor(user.role));
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(username.trim(), password);
      nav(defaultRouteFor(u.role));
    } catch (er) {
      setErr(t("login_error"));
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (u) => {
    setUsername(u);
    setPassword(DEMO_PASSWORD);
    setErr("");
  };

  return (
    <div className="min-h-screen flex flex-col items-stretch md:flex-row hag-motif-bg">
      <div className="hidden md:flex md:flex-1 flex-col justify-between p-12 relative overflow-hidden" style={{ background: "var(--brand-navy)" }}>
        <div>
          <div className="text-[#C8A86B] font-black text-4xl tracking-tight">HAG</div>
          <div className="text-white/70 text-sm tracking-[0.3em] uppercase mt-1">Carpets Ops</div>
        </div>
        <div>
          <div className="text-white text-4xl font-bold leading-tight">Loom-to-ledger,<br /><span className="text-[#C8A86B]">from Mirzapur.</span></div>
          <div className="text-white/60 text-sm mt-4 max-w-md">Order tracker, Daura inspection, factory ops, and a magic-link vendor portal — offline, bilingual, and honest about the loom.</div>
        </div>
        <div className="text-white/50 text-xs tracking-widest uppercase">Phase 1 · 2 · 3 shipped</div>
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ background: "repeating-linear-gradient(45deg, transparent 0 20px, #C8A86B 20px 21px)" }} />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} data-testid="login-form" className="hag-surface p-8 w-full max-w-md">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="md:hidden font-black text-3xl" style={{ color: "var(--brand-navy)" }}>HAG</div>
              <h1 className="text-2xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>{t("login")}</h1>
              <p className="text-sm text-[#7C8E9E] mt-1">{t("welcome")}</p>
            </div>
            <button
              type="button"
              data-testid="login-lang-toggle"
              onClick={toggle}
              className="tap-target px-3 rounded-md border border-[#1B3A5C]/10 hover:bg-[#FAF6F0] flex items-center gap-1 text-sm"
            >
              <Languages size={16} /> {lang === "en" ? "हिन्दी" : "EN"}
            </button>
          </div>

          <label className="block text-xs uppercase tracking-widest font-bold text-[#4A627A] mt-4">{t("username")}</label>
          <input
            data-testid="login-username-input"
            className="mt-1 w-full h-12 px-3 rounded-md border border-[#1B3A5C]/15 focus:border-[#1B3A5C] focus:outline-none text-base"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
          />

          <label className="block text-xs uppercase tracking-widest font-bold text-[#4A627A] mt-4">{t("password")}</label>
          <input
            data-testid="login-password-input"
            type="password"
            className="mt-1 w-full h-12 px-3 rounded-md border border-[#1B3A5C]/15 focus:border-[#1B3A5C] focus:outline-none text-base"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          {err && <div data-testid="login-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2">{err}</div>}

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy}
            className="mt-6 w-full h-12 rounded-md font-semibold text-white flex items-center justify-center gap-2"
            style={{ background: "var(--brand-navy)" }}
          >
            {busy && <Loader2 size={16} className="animate-spin" />} {t("login")}
          </button>

          <div className="mt-6 border-t border-[#1B3A5C]/10 pt-4">
            <button
              type="button"
              data-testid="login-demo-toggle"
              onClick={() => setShowDemo((s) => !s)}
              className="w-full text-left text-xs font-semibold text-[#4A627A] hover:text-[#1B3A5C] flex items-center justify-between"
            >
              <span>
                {lang === "hi" ? "डेमो अकाउंट्स दिखाएँ (एक क्लिक भरें)" : "Show demo accounts (tap to fill)"}
              </span>
              <span className="text-[10px] text-[#7C8E9E]">{showDemo ? "▲" : "▼"}</span>
            </button>

            {showDemo && (
              <div className="mt-3 space-y-3" data-testid="login-demo-panel">
                {DEMO_GROUPS.map((g) => (
                  <div key={g.label_en}>
                    <div className="text-[10px] uppercase tracking-widest text-[#C8A86B] font-bold mb-1">
                      {lang === "hi" ? g.label_hi : g.label_en}
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {g.accounts.map((a) => (
                        <button
                          key={a.u}
                          type="button"
                          data-testid={`login-demo-${a.u}`}
                          onClick={() => fillDemo(a.u)}
                          className="text-left px-2 py-1.5 rounded-md border border-[#1B3A5C]/10 hover:bg-[#FAF6F0] hover:border-[#C8A86B]"
                        >
                          <div className="font-mono text-xs font-bold" style={{ color: "var(--brand-navy)" }}>{a.u}</div>
                          <div className="text-[10px] text-[#7C8E9E] truncate">{lang === "hi" ? a.role_hi : a.role_en}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="text-[10px] text-[#7C8E9E] pt-1">
                  {lang === "hi"
                    ? `पासवर्ड सभी अकाउंट्स के लिए: `
                    : `Password for all accounts: `}
                  <code className="bg-[#FAF6F0] px-1 rounded">{DEMO_PASSWORD}</code>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export function defaultRouteFor(role) {
  return {
    inspector: "/inspector/route",
    supervisor: "/supervisor/assignment-queue",
    coordinator: "/coordinator/po-register",
    merchandiser: "/merch/my-pos",
    director: "/director/dashboard",
    // Phase 2 factory roles
    prod_qc_coordinator: "/factory/wip",
    qc_officer: "/factory/wip",
    wash_master: "/factory/finishing",
    finisher: "/factory/finishing",
    stores_coordinator: "/factory/grn",
    vendor_mg: "/mg/dashboard",
    ric_staff: "/ric/dashboard",
    ric_supervisor: "/ric/dashboard",
    security_guard: "/guard/today",
  }[role] || "/";
}
